import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useToastContext, Button, ButtonLink } from '@resideo/blueprint-oneportal';
import { useTranslation } from 'react-i18next';
import type { UpdateUserInviteDataModel } from '@resideo/web-integration-services-api-client';
import apiService from '@/services/apiService';
import type { ConsumerLocationDetail } from '@/services/apiService';
import { RemoveLocationConfirmModal } from './RemoveLocationConfirmModal';
import styles from './EmployeeDetailLocationsTab.module.css';

export interface EmployeeDetailLocationsTabProps {
  consumerAccountId: string | null;
  consumerUserId: string | undefined;
  email: string;
  userName: string;
  invitedUserId: string | null;
  onManageAccess: (locationId: string, locationName: string) => void;
  onAddLocation: () => void;
  canManageUsers?: boolean;
}

function formatAddress(location: ConsumerLocationDetail): string {
  const a = location.address;
  if (!a) return '';
  const parts = [
    a.addressLine1,
    a.city,
    [a.stateProvinceRegionCode, a.zipPostalCode].filter(Boolean).join(' '),
  ].filter(Boolean);
  return parts.join(', ');
}

export function EmployeeDetailLocationsTab({
  consumerAccountId,
  consumerUserId,
  email,
  userName,
  invitedUserId,
  onManageAccess,
  onAddLocation,
  canManageUsers = true,
}: EmployeeDetailLocationsTabProps) {
  const queryClient = useQueryClient();
  const { addToast } = useToastContext();
  const { t } = useTranslation();

  const [pendingRemoveLocation, setPendingRemoveLocation] = useState<{ id: string; name: string } | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);

  const { data, isFetching, error } = useQuery({
    queryKey: ['consumerUserDetail', consumerAccountId, consumerUserId],
    queryFn: () => apiService.getConsumerUserDetail(consumerAccountId!, consumerUserId!),
    enabled: !!consumerAccountId && !!consumerUserId,
    refetchOnWindowFocus: false,
  });

  const allLocations: ConsumerLocationDetail[] = data?.data?.consumerAccount?.locations ?? [];
  const locations = allLocations.filter(loc => (loc.consumerDevices ?? []).length > 0);
  const role: string = data?.data?.role ?? '';
  const isAdmin = role === 'ADMIN';

  async function handleConfirmRemove() {
    if (!pendingRemoveLocation || !consumerAccountId) return;
    setIsRemoving(true);
    setRemoveError(null);
    try {
      const remainingDeviceIds = locations
        .filter(loc => loc.id !== pendingRemoveLocation.id)
        .flatMap(loc =>
          (loc.consumerDevices ?? []).map(d => d.device?.deviceId ?? d.id),
        )
        .filter(Boolean) as string[];

      const now = new Date();
      const expiresAt = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000).toISOString();

      const inviteDataInfo: UpdateUserInviteDataModel['inviteDataInfo'] = {
        role: role as any,
        sendToEmail: email,
        deviceIds: remainingDeviceIds,
        ...(role === 'GUEST' && {
          scheduleInfo: {
            weekDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'] as any,
            startDate: now.toISOString(),
            endDate: new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000).toISOString(),
            startTime: '00:01',
            endTime: '23:59',
          },
        }),
      };

      const body: UpdateUserInviteDataModel = {
        invitedUserId: invitedUserId ?? undefined,
        inviteDataInfo,
        emailTemplateInfo: {
          templateLanguage: 'en-US',
          baseUrl: `${import.meta.env.VITE_RESIDEO_ID_URL}/mobile`,
          expiresAt,
        },
      };

      await apiService.updateInvite(consumerAccountId, body);
      await queryClient.invalidateQueries({ queryKey: ['consumerUserDetail', consumerAccountId, consumerUserId] });
      addToast({ message: t('removeLocationModal.locationRemoved'), toastType: 'Success', zIndex: '9999' });
      setPendingRemoveLocation(null);
    } catch {
      setRemoveError('Failed to remove location. Please try again.');
    } finally {
      setIsRemoving(false);
    }
  }

  if (isFetching) {
    return <div className={styles.state}>Loading locations…</div>;
  }

  if (error) {
    return <div className={`${styles.state} ${styles.error}`}>Unable to load locations. Please try again later.</div>;
  }

  return (
    <div className={styles.container}>
      <div className={styles.toolbar}>
        <span className={styles.title}>Locations ({locations.length})</span>
        {canManageUsers && (
          <Button
            variant="highlightSecondary"
            onClick={onAddLocation}
            className={styles.addLocationBtn}
          >
            {t('employeeDetail.addLocations')}
          </Button>
        )}
      </div>

      {removeError && (
        <div className={`${styles.state} ${styles.error}`}>{removeError}</div>
      )}

      {locations.length === 0 ? (
        <div className={styles.state}>No locations assigned yet.</div>
      ) : (
        <div className={styles.locationList}>
          {locations.map(location => {
            const address = formatAddress(location);

            return (
              <div key={location.id} className={styles.locationCard}>
                <div className={styles.locationInfo}>
                  <span className={styles.locationName}>{location.name}</span>
                  {address && (
                    <span className={styles.locationAddress}>{address}</span>
                  )}
                </div>
                <div className={styles.locationActions}>
                  {!canManageUsers ? null : isAdmin ? (
                    // Site Manager — show "View Access" only (no Remove)
                    <ButtonLink
                      onClick={() => onManageAccess(location.id, location.name)}
                      className={styles.viewAccessLink}
                    >
                      {t('employeeDetail.viewAccess')}
                    </ButtonLink>
                  ) : (
                    <>
                      <ButtonLink
                        onClick={() => onManageAccess(location.id, location.name)}
                        className={styles.manageAccessLink}
                      >
                        {t('employeeDetail.manageAccess')}
                      </ButtonLink>
                      <ButtonLink
                        onClick={() => setPendingRemoveLocation({ id: location.id, name: location.name })}
                        className={styles.removeLink}
                      >
                        {t('employeeDetail.remove')}
                      </ButtonLink>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {pendingRemoveLocation && (
        <RemoveLocationConfirmModal
          locationName={pendingRemoveLocation.name}
          userName={userName}
          isRemoving={isRemoving}
          onConfirm={handleConfirmRemove}
          onCancel={() => {
            if (!isRemoving) {
              setPendingRemoveLocation(null);
              setRemoveError(null);
            }
          }}
        />
      )}
    </div>
  );
}
